/* eslint-disable no-case-declarations */
import { ComponentData } from "@/types/builder";

export const generateCSS = (
  components: ComponentData[],
  pageName: string
): string => {
  const styles: string[] = [];
  const componentSelectors: string[] = [];

  // Generate CSS for each component
  const processComponent = (
    component: ComponentData,
    index: number,
    parentSelector?: string
  ) => {
    const { type, properties } = component;
    const selector = parentSelector
      ? `${parentSelector} .component-${index}`
      : `.component-${index}`;

    componentSelectors.push(selector);

    const cssProps: string[] = [];

    // Convert properties to CSS
    if (properties.backgroundColor) {
      cssProps.push(`  background-color: ${properties.backgroundColor};`);
    }
    if (properties.textColor) {
      cssProps.push(`  color: ${properties.textColor};`);
    }
    if (properties.paddingBottom) {
      cssProps.push(`  padding-bottom: ${properties.paddingBottom};`);
    }
    if (properties.paddingLeft) {
      cssProps.push(`  padding-left: ${properties.paddingLeft};`);
    }
    if (properties.paddingRight) {
      cssProps.push(`  padding-right: ${properties.paddingRight};`);
    }
    if (properties.paddingTop) {
      cssProps.push(`  padding-top: ${properties.paddingTop};`);
    }
    if (properties.marginBottom) {
      cssProps.push(`  margin-bottom: ${properties.marginBottom};`);
    }
    if (properties.marginLeft) {
      cssProps.push(`  margin-left: ${properties.marginLeft};`);
    }
    if (properties.marginRight) {
      cssProps.push(`  margin-right: ${properties.marginRight};`);
    }

    if (properties.marginTop) {
      cssProps.push(`  margin-top: ${properties.marginTop};`);
    }
    if (properties.borderRadius) {
      cssProps.push(`  border-radius: ${properties.borderRadius};`);
    }
    if (properties.fontSize) {
      cssProps.push(`  font-size: ${properties.fontSize};`);
    }
    if (properties.fontWeight) {
      cssProps.push(`  font-weight: ${properties.fontWeight};`);
    }
    if (properties.textAlign) {
      cssProps.push(`  text-align: ${properties.textAlign};`);
    }

    // Add component-specific styles
    switch (type) {
      case "hero":
        cssProps.push(`  display: flex;`);
        cssProps.push(`  align-items: center;`);
        cssProps.push(`  justify-content: center;`);
        cssProps.push(`  min-height: 400px;`);
        cssProps.push(`  position: relative;`);
        break;

      case "button":
        cssProps.push(`  display: inline-block;`);
        cssProps.push(`  cursor: pointer;`);
        cssProps.push(`  border: none;`);
        cssProps.push(`  text-decoration: none;`);
        cssProps.push(`  transition: all 0.3s ease;`);
        break;

      case "card":
        cssProps.push(`  border: 1px solid #e5e7eb;`);
        cssProps.push(`  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);`);
        cssProps.push(`  transition: box-shadow 0.3s ease;`);
        break;

      case "image":
        cssProps.push(`  width: 100%;`);
        cssProps.push(`  height: auto;`);
        cssProps.push(`  object-fit: cover;`);
        break;

      case "container":
        cssProps.push(`  display: block;`);
        break;
    }

    if (cssProps.length > 0) {
      styles.push(`${selector} {\n${cssProps.join("\n")}\n}`);
    }

    // Add hover effects for interactive elements
    if (type === "button") {
      styles.push(
        `${selector}:hover {\n  opacity: 0.9;\n  transform: translateY(-1px);\n}`
      );
    }
    if (type === "card") {
      styles.push(
        `${selector}:hover {\n  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);\n}`
      );
    }

    // Process children
    if (component.children) {
      component.children.forEach((child, childIndex) => {
        processComponent(child, childIndex, selector);
      });
    }
  };

  // Process all components
  components.forEach((component, index) => {
    processComponent(component, index);
  });

  // Generate complete CSS
  const cssHeader = `/* Generated CSS for ${pageName} */\n/* Created with HTML Page Builder */\n\n`;

  const resetCSS = `/* Reset and Base Styles */\n* {\n  box-sizing: border-box;\n  margin: 0;\n  padding: 0;\n}\n\nbody {\n  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;\n  line-height: 1.6;\n  color: #333;\n}\n\n`;

  const responsiveCSS = `\n/* Responsive Design */\n@media (max-width: 768px) {\n  .component-hero {\n    min-height: 300px;\n    padding: 2rem 1rem;\n  }\n  \n  .component-hero h1 {\n    font-size: 2rem;\n  }\n  \n  .component-card {\n    margin: 1rem 0;\n  }\n}\n`;

  return cssHeader + resetCSS + styles.join("\n\n") + responsiveCSS;
};

export const generateHTML = (
  components: ComponentData[],
  pageName: string
): string => {
  const generateComponentHTML = (
    component: ComponentData,
    index: number
  ): string => {
    const { type, properties } = component;
    const className = `component-${index}`;

    switch (type) {
      case "hero":
        return `
  <section class="${className} component-hero">
    <div class="hero-content">
      ${properties.heading ? `<h1>${properties.heading}</h1>` : ""}
      ${
        properties.subheading
          ? `<p class="hero-subtitle">${properties.subheading}</p>`
          : ""
      }
      ${
        properties.buttonText
          ? `<a href="${properties.href || "#"}" class="hero-button">${
              properties.buttonText
            }</a>`
          : ""
      }
    </div>
  </section>`;

      case "text":
        return `
  <div class="${className} component-text">
    <p>${properties.text || "Text content"}</p>
  </div>`;

      case "button":
        return `
  <div class="${className} component-button">
    <a href="${properties.href || "#"}" target="${
          properties.target || "_blank"
        }">${properties.buttonText || "Button"}</a>
  </div>`;

      case "card":
        return `
  <div class="${className} component-card">
    ${properties.heading ? `<h3>${properties.heading}</h3>` : ""}
    ${properties.text ? `<p>${properties.text}</p>` : ""}
  </div>`;

      case "image":
        return `
  <div class="${className} component-image">
    <img src="${
      properties.imageSrc || "https://via.placeholder.com/400x200"
    }" alt="${properties.imageAlt || "Image"}" />
  </div>`;

      case "container":
        const childrenHTML =
          component.children
            ?.map((child, childIndex) =>
              generateComponentHTML(child, childIndex)
            )
            .join("") || "";
        return `
  <div class="${className} component-container">
    ${childrenHTML}
  </div>`;

      default:
        return `<div class="${className}">Unknown component</div>`;
    }
  };

  const componentsHTML = components
    .map((component, index) => generateComponentHTML(component, index))
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pageName}</title>
  <link rel="stylesheet" href="styles.css">
</head>
<body>
${componentsHTML}
</body>
</html>`;
};
